import React, { useEffect, useState } from 'react';
import { getActiveStaff, getStaffMonthly } from '../services/api';

const FULL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const AVATAR_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16'];
const _cy = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => _cy - 3 + i);
function initials(n) { return n?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '?'; }

export default function Reports() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getActiveStaff().then(r => setStaff(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!staff.length) return;
    setLoading(true);
    Promise.all(staff.map(s =>
      getStaffMonthly(s.id, month, year).then(r => {
        const att = r.data || [];
        const present = att.filter(a => a.status === 'PRESENT').length;
        const absent = att.filter(a => a.status === 'ABSENT').length;
        const halfDay = att.filter(a => a.status === 'HALF_DAY').length;
        const leave = att.filter(a => a.status === 'LEAVE').length;
        const totalDays = new Date(year, month, 0).getDate();
        const rate = Math.round((present / totalDays) * 100);
        return { ...s, present, absent, halfDay, leave, totalDays, rate };
      })
    )).then(data => { setSummary(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [month, year, staff.length]);

  const totalPresent = summary.reduce((s, r) => s + r.present, 0);
  const avgRate = summary.length ? Math.round(summary.reduce((s, r) => s + r.rate, 0) / summary.length) : 0;
  const sorted = [...summary].sort((a, b) => b.rate - a.rate);
  const best = sorted[0] || null;
  const worst = sorted[sorted.length - 1] || null;

  const exportCsv = () => {
    const rows = [['Name','Emp ID','Dept','Present','Absent','Half Day','Leave','Total','Rate%']];
    summary.forEach(s => rows.push([s.name, s.employeeCode, s.department?.name||'-', s.present, s.absent, s.halfDay, s.leave, s.totalDays, s.rate]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `attendance-${FULL_MONTHS[month-1]}-${year}.csv`;
    a.click();
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Attendance Reports</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{FULL_MONTHS[month-1]} {year} overview</div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff', cursor: 'pointer' }}>
            {FULL_MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff', cursor: 'pointer' }}>
            {YEARS.map(y => <option key={y}>{y}</option>)}
          </select>
          <button onClick={exportCsv} style={{ padding: '9px 20px', borderRadius: 10, background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 12px rgba(5,150,105,0.3)' }}>
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {[
          { icon: '📊', label: 'Avg Attendance Rate', value: `${avgRate}%`, color: avgRate > 80 ? '#10b981' : avgRate > 60 ? '#f59e0b' : '#ef4444', bg: avgRate > 80 ? '#f0fdf4' : avgRate > 60 ? '#fffbeb' : '#fff1f2' },
          { icon: '✅', label: 'Total Present Days', value: totalPresent, color: '#3b82f6', bg: '#eff6ff' },
          { icon: '🏆', label: 'Best Performer', value: best?.name?.split(' ')[0] || '-', sub: best ? `${best.rate}%` : '', color: '#10b981', bg: '#f0fdf4' },
          { icon: '⚠️', label: 'Needs Attention', value: worst?.name?.split(' ')[0] || '-', sub: worst ? `${worst.rate}%` : '', color: '#ef4444', bg: '#fff1f2' },
        ].map(c => (
          <div key={c.label} style={{ flex: 1, background: '#fff', borderRadius: 14, padding: '20px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderTop: `4px solid ${c.color}`, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: -6, top: -6, width: 54, height: 54, borderRadius: '50%', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{c.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{c.value}</div>
            {c.sub && <div style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{c.sub}</div>}
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14, marginBottom: 20 }}>Attendance Rate by Staff — {FULL_MONTHS[month-1]} {year}</div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>Loading...
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, minWidth: summary.length * 70, height: 220, paddingBottom: 36, paddingTop: 16 }}>
              {summary.map((s, i) => {
                const barColor = s.rate > 80 ? '#10b981' : s.rate > 60 ? '#f59e0b' : '#ef4444';
                const barH = Math.max(4, (s.rate / 100) * 160);
                return (
                  <div key={s.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: barColor }}>{s.rate}%</div>
                    <div style={{
                      width: '80%', height: barH, background: barColor,
                      borderRadius: '6px 6px 0 0', minHeight: 4,
                      transition: 'height 0.6s ease',
                      boxShadow: `0 -2px 8px ${barColor}40`,
                    }} />
                    <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center', maxWidth: 56, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 4 }}>{s.name?.split(' ')[0]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detailed Table */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Detailed Breakdown</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
              {['Employee','Dept','Present','Absent','Half Day','Leave','Total','Rate'].map(h => (
                <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 12, letterSpacing: 0.3 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading...</td></tr>
            ) : sorted.map((s, i) => {
              const ac = AVATAR_COLORS[i % AVATAR_COLORS.length];
              const rateColor = s.rate > 80 ? '#10b981' : s.rate > 60 ? '#f59e0b' : '#ef4444';
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: ac, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 12, flexShrink: 0 }}>{initials(s.name)}</div>
                      <div>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.employeeCode}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '13px 20px', color: '#475569', fontSize: 12 }}>{s.department?.name || '-'}</td>
                  <td style={{ padding: '13px 20px', fontWeight: 700, color: '#10b981' }}>{s.present}</td>
                  <td style={{ padding: '13px 20px', fontWeight: 700, color: '#ef4444' }}>{s.absent}</td>
                  <td style={{ padding: '13px 20px', color: '#f59e0b', fontWeight: 600 }}>{s.halfDay}</td>
                  <td style={{ padding: '13px 20px', color: '#3b82f6' }}>{s.leave}</td>
                  <td style={{ padding: '13px 20px', color: '#64748b' }}>{s.totalDays}</td>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 3, minWidth: 60 }}>
                        <div style={{ width: `${s.rate}%`, height: '100%', background: rateColor, borderRadius: 3, transition: 'width 0.5s' }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: rateColor, minWidth: 38 }}>{s.rate}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>No data available</div>
        )}
      </div>
    </div>
  );
}
